import { AtlasCoord, JsonDictionaryWrapper } from './utils';

export enum Resource {
    Stone,
    Steel,
    SteelOre,
    Uranium,
    FuelRod,
    PavingSlab,
}

const resources = [
    Resource.Stone,
    Resource.Steel,
    Resource.SteelOre,
    Resource.Uranium,
    Resource.FuelRod,
    Resource.PavingSlab
]

export function resource_name(r: Resource): string {
    switch (r) {
        case Resource.Stone:
            return "Stone"

        case Resource.Steel:
            return "Steel"

        case Resource.SteelOre:
            return "Steel Ore"

        case Resource.Uranium:
            return "Uranium"

        case Resource.FuelRod:
            return "FuelRod"

        case Resource.PavingSlab:
            return "PavingSlab"
    }
}

export function resource_image(r: Resource): AtlasCoord {
    switch (r) {
        case Resource.Stone:
            return { i: 0, j: 0 }

        case Resource.Steel:
            return { i: 1, j: 0 }

        case Resource.Uranium:
            return { i: 2, j: 0 }

        case Resource.FuelRod:
            return { i: 3, j: 0 }

        case Resource.SteelOre:
            return { i: 15, j: 0 }

        case Resource.PavingSlab:
            return { i: 14, j: 0 }
    }
}

export class Purse {
    // indexed by resource index
    private count: number[]

    private log_global: boolean

    public did_change: ((p: Purse) => void) | null = null

    public static create_json(obj: JsonDictionaryWrapper): Purse {
        let p = new Purse(obj.get_boolean("log_global"))
        p.count = obj.get_number_array("count")
        return p
    }

    constructor(log_with_global: boolean) {
        this.log_global = log_with_global
        this.count = []
        for (var i = 0; i < resources.length; i += 1) {
            this.count.push(0)
        }
    }

    private static _global: Purse = new Purse(false)
    static get global(): Purse {
        return Purse._global
    }

    // what resource types are in the purse
    public available_types(): Resource[] {
        var r: Resource[] = []
        for (var ri = 0; ri < resources.length; ri += 1) {
            if (this.count[ri] > 0) {
                r.push(resources[ri])
            }
        }
        return r
    }

    // enumerate all individual resources (unlike available_types this would return multiples)
    public enumerate(): Resource[] {
        var r: Resource[] = []
        for (var ri = 0; ri < resources.length; ri += 1) {
            for (var rj = 0; rj < this.count[ri]; rj += 1) {
                r.push(resources[ri])
            }
        }
        return r
    }

    // clear the purse (in a way that registers with the global)
    public clear(): void {
        for (let r of this.enumerate()) {
            this.subtract_resource(1, r);
        }
    }

    // add a resource to the purse
    public add_resource(count: number, res: Resource): void {
        for (var ri = 0; ri < resources.length; ri += 1) {
            if (resources[ri] == res) {
                this.count[ri] += count
            }
        }

        if (this.log_global) {
            Purse._global.add_resource(count, res);
        }

        if (this.did_change != null) {
            this.did_change(this)
        }
    }

    // remove a resource from the purse
    public subtract_resource(count: number, res: Resource): void {
        for (var ri = 0; ri < resources.length; ri += 1) {
            if (resources[ri] == res) {
                this.count[ri] -= count
            }
        }

        if (this.log_global) {
            Purse._global.subtract_resource(count, res)
        }

        if (this.did_change != null) {
            this.did_change(this)
        }
    }

    public get_count(r: Resource): number {
        for (var ri = 0; ri < resources.length; ri += 1) {
            if (resources[ri] == r) {
                return this.count[ri]
            }
        }

        throw new Error("get_count: never found resource")
    }

    public empty(): boolean {
        for (var ri = 0; ri < resources.length; ri += 1) {
            if (this.count[ri] > 0) {
                return false
            }
        }

        return true
    }

    // add the entire contents of another purse
    public add(other: Purse): void {
        for (var ri = 0; ri < resources.length; ri += 1) {
            this.add_resource(other.count[ri], resources[ri])
        }
    }

    // remove an entire other purse from this one
    public subtract(other: Purse): void {
        for (var ri = 0; ri < resources.length; ri += 1) {
            this.subtract_resource(other.count[ri], resources[ri])
        }
    }

    // check we have as much
    public greater_or_equal(other: Purse): boolean {
        for (var ri = 0; ri < resources.length; ri += 1) {
            if (this.count[ri] < other.count[ri]) {
                return false
            }
        }

        return true
    }

    public copy(log_global: boolean): Purse {
        let p = new Purse(log_global)
        p.add(this)
        return p
    }

    public equal_to(other: Purse): boolean {
        for (var ri = 0; ri < resources.length; ri += 1) {
            if (this.count[ri] != other.count[ri]) {
                return false
            }
        }

        return true
    }

    // log our contents
    public description(): string {
        var s = ""
        for (var ri = 0; ri < resources.length; ri += 1) {
            const resource = resources[ri]
            if (s != "") {
                s += ", "
            }
            s += resource_name(resource)
            s += ": "
            s += this.count[ri]
        }
        return s
    }

    // lines for displaying cost
    public cost_lines(): string[] {
        var lines: string[] = []

        for (var ri = 0; ri < resources.length; ri += 1) {
            if (this.count[ri] == 0) {
                continue
            }
            const resource = resources[ri]

            var s = "" + this.count[ri]
            s += " "
            s += resource_name(resource)
            lines.push(s)
        }

        return lines
    }
}
